import { cn } from '@/lib/utils';
import type { Status } from '@/models/types';
import dayjs from 'dayjs';
import { CircleSmall } from 'lucide-react';
import { Badge } from './badge';

export type DealTimeStatusBadgeProps = {
  status: Status;
  dateString: string;
  className?: string;
  hideCopy?: boolean;
};

const DEFAULT_CLASSES =
  'text-black bg-white  text-sm shadow-none rounded-full border-foreground/10 pointer-events-none pl-1 pr-2 text-xs';

export const DealTimeStatusBadge = ({
  status,
  dateString,
  className,
  hideCopy = false,
}: DealTimeStatusBadgeProps) => {
  if (status === 'new') {
    // if the deal is under 3 hours old, don't show the badge
    const isUnder3HoursOld = dayjs(dateString).isAfter(
      dayjs().subtract(3, 'hours'),
    );
    if (isUnder3HoursOld) {
      return null;
    }

    // if the deal is over 3 hours old and under 24 hours old, show the badge
    const isOver3HoursOldAndUnder24HoursOld =
      dayjs(dateString).isBefore(dayjs().subtract(3, 'hours')) &&
      dayjs(dateString).isAfter(dayjs().subtract(1, 'day'));
    if (isOver3HoursOldAndUnder24HoursOld) {
      return (
        <Badge className={cn(DEFAULT_CLASSES, className)}>
          <CircleSmall
            fill="var(--color-orange-500)"
            stroke="var(--color-orange-500)"
            className="size-4 mr-1"
          />
          {hideCopy ? '3h+' : 'Over 3h'}
        </Badge>
      );
    }

    // if the deal is over 24 hours old, show the badge
    const isOver24HoursOld = dayjs(dateString).isBefore(
      dayjs().subtract(1, 'day'),
    );
    if (isOver24HoursOld) {
      return (
        <Badge className={cn(DEFAULT_CLASSES, className)}>
          <CircleSmall
            fill="var(--color-red-500)"
            stroke="var(--color-red-500)"
            className="size-4 mr-1"
          />
          {hideCopy ? '24h+' : 'Over 24h'}
        </Badge>
      );
    }
  }

  if (status === 'in_progress') {
    // if the deal is over 36 hours old and under 72 hours old, show the badge
    const isOver36HoursOldAndUnder72HoursOld =
      dayjs(dateString).isBefore(dayjs().subtract(36, 'hours')) &&
      dayjs(dateString).isAfter(dayjs().subtract(3, 'day'));
    if (isOver36HoursOldAndUnder72HoursOld) {
      return (
        <Badge className={cn(DEFAULT_CLASSES, className)}>
          <CircleSmall
            fill="var(--color-orange-500)"
            stroke="var(--color-orange-500)"
            className="size-4 mr-1"
          />
          {hideCopy ? '36h+' : 'Over 36h'}
        </Badge>
      );
    }

    // if the deal is over 72 hours old, show the badge
    const isOver72HoursOld = dayjs(dateString).isBefore(
      dayjs().subtract(3, 'day'),
    );
    if (isOver72HoursOld) {
      return (
        <Badge className={cn(DEFAULT_CLASSES, className)}>
          <CircleSmall
            fill="var(--color-red-500)"
            stroke="var(--color-red-500)"
            className="size-4 mr-1"
          />
          {hideCopy ? '72h+' : 'Over 72h'}
        </Badge>
      );
    }
  }

  return null;
};
